import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Filter } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { portfolioProjects } from '../data/mockData';

const categories = ['All', 'Brand Identity', 'Web Design', 'Print Design', 'Packaging Design', 'Illustration'];

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hoveredProject, setHoveredProject] = useState(null);

  const filtered = activeFilter === 'All'
    ? portfolioProjects
    : portfolioProjects.filter((p) => p.category === activeFilter);

  return (
    <main className="pt-24 pb-0">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">Portfolio</span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl text-[#1a1a1a] mt-4 tracking-tight max-w-3xl"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            Selected work that speaks volumes
          </h1>
          <p className="text-base text-[#5f6368] mt-6 max-w-2xl leading-relaxed">
            A curated collection of projects where strategic thinking meets creative execution.
            Each piece represents a unique collaboration and a story worth telling.
          </p>
        </ScrollReveal>
      </section>

      {/* Filters */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-12">
        <ScrollReveal delay={200}>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={16} className="text-[#5f6368] mr-2" />
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm transition-all duration-200 ${
                  activeFilter === cat
                    ? 'bg-[#1a1a1a] text-white'
                    : 'bg-[#f8f9fa] text-[#5f6368] hover:bg-[#e8eaed]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </ScrollReveal>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {filtered.map((project, i) => (
              <ScrollReveal key={project.id} delay={i * 100}>
                <div
                  className="group relative cursor-pointer"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  <div className="rounded-2xl overflow-hidden border border-[#e8eaed] bg-white">
                    <div className="relative aspect-[16/11] overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div
                        className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300 ${
                          hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center">
                          <ArrowUpRight size={22} className="text-[#1a1a1a]" />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-[#5f6368] tracking-wide uppercase">{project.category}</span>
                        <span className="text-xs text-[#5f6368]">{project.year}</span>
                      </div>
                      <h3 className="text-xl font-medium text-[#1a1a1a] mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                        {project.title}
                      </h3>
                      <p className="text-sm text-[#5f6368] leading-relaxed mb-4">{project.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {project.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-3 py-1 rounded-full bg-[#f8f9fa] text-[#5f6368]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </motion.div>
        </AnimatePresence>
      </section>
    </main>
  );
};

export default Portfolio;
